import {API_HOST, DEFAULT_HEADER, } from "../constants/axios";
import axios from "axios";
import {Cookies} from "react-cookie";
import {EventSourcePolyfill} from "event-source-polyfill";

const cookies = new Cookies();

export const instance = axios.create({
  baseURL: API_HOST,
});

/**
 * 다른 탭에서 채널을 전환했을 때 이 탭의 **모든 API 요청**을 중단한다.
 *
 * 배경(2026-07-27 인시던트): 채널 컨텍스트는 localStorage("channelState") 하나이고,
 * 요청 헤더 Channel-Id 는 매 요청마다 그 값을 읽는다(getTokenHeader).
 * 따라서 A 채널 화면을 열어둔 채 다른 탭에서 B 채널로 전환하면,
 * 이 탭은 화면·데이터는 A 인데 요청만 B 로 나간다.
 *   - 쓰기: 다른 고객사에 저장되는 사고
 *   - 읽기: 권한이 있으면 **다른 고객사 데이터가 조용히 조회**되고, 없으면 500 →
 *           checkAuthorization 이 /notfound 로 보내고 `res.data`(undefined) 구조분해에서 TypeError 연쇄
 * 그래서 읽기·쓰기를 가리지 않고 전부 막고, 사용자에게 새로고침을 안내한다.
 *
 * 채널 전환은 같은 탭에서 reload 를 동반하므로(Layout.js) 정상 전환은 걸리지 않는다.
 * storage 이벤트는 "변경을 일으킨 탭"에서는 발생하지 않으므로,
 * 값 비교가 아닌 이벤트 수신 여부로 판정해 같은 탭에서의 정상 전환을 오탐하지 않는다.
 */
let externalChannelSwitch = null;

const NOTICE_ID = "omoplay-channel-switch-notice";

/**
 * 채널 컨텍스트와 무관한 화면 — 타 탭에서 채널이 바뀌어도 차단하지 않는다.
 *
 * `/sysadmin` 이 여기 해당하는 근거(2026-07-28 확인):
 *  - 권한 판정이 `account.id` 만 사용한다 (`AccountPermissionService.isSystemAdmin`) → 채널을 바꿔도 권한 불변
 *  - sysadmin 컨트롤러 8종 모두 `account.channelId` 를 쓰지 않고 대상 channelId 를 파라미터로 명시적으로 받는다
 *  → Channel-Id 헤더가 바뀌어도 조회·저장 대상이 달라지지 않는다.
 *
 * 운영상 이유: 관리자가 채널 세팅과 **병행해 참고용으로 sysadmin 화면을 띄워두는** 경우가 많다.
 * 여기까지 끊고 홈으로 보내면 작업 중이던 내용이 사라진다.
 */
const CHANNEL_AGNOSTIC_PATHS = ["/sysadmin"];

const isChannelAgnosticPage = () =>
  typeof window !== "undefined" &&
  CHANNEL_AGNOSTIC_PATHS.some(
    (p) => window.location.pathname === p || window.location.pathname.startsWith(p + "/")
  );

/** 채널 id 를 화면에 넣기 전 안전 문자만 남긴다(HTML 주입 방지). */
const safeChannelId = (v) => String(v ?? "-").replace(/[^0-9A-Za-z_-]/g, "") || "-";

/** 차단된 요청 수 — 사용자가 "저장이 됐나?" 오해하지 않도록 배너에 표시한다. */
let blockedRequestCount = 0;

/**
 * 안내는 **상단 고정 배너**(모달 아님)로 띄운다.
 * 모달로 덮으면 작성 중인 내용을 선택·복사할 수 없어 "복사 후 새로고침" 안내가 성립하지 않는다.
 * 요청 차단은 fetch 계층에서 이미 하고 있으므로, UI 는 화면을 막을 필요가 없다.
 */
const showChannelSwitchNotice = () => {
  if (typeof document === "undefined") return;

  const existing = document.getElementById(NOTICE_ID);
  if (existing) {
    const countEl = document.getElementById(NOTICE_ID + "-count");
    if (countEl) {
      countEl.textContent = blockedRequestCount > 0 ? ` (차단된 요청 ${blockedRequestCount}건)` : "";
    }
    return;
  }

  const from = safeChannelId(externalChannelSwitch?.from);
  const to = safeChannelId(externalChannelSwitch?.to);

  const bar = document.createElement("div");
  bar.id = NOTICE_ID;
  bar.setAttribute("style",
    "position:fixed;top:0;left:0;right:0;z-index:2147483647;background:#b91c1c;color:#fff;" +
    "padding:10px 16px;display:flex;align-items:center;gap:12px;flex-wrap:wrap;" +
    "font-size:13px;line-height:1.6;box-shadow:0 2px 12px rgba(0,0,0,.3);");
  bar.innerHTML =
    '<div style="flex:1;min-width:280px;">' +
      '<b>다른 탭에서 채널이 전환되었습니다 (' + from + ' → ' + to + ').</b> ' +
      '이 화면은 이전 채널의 데이터라, 다른 고객사에 저장·조회되지 않도록 <b>이 탭의 요청을 모두 중단</b>했습니다' +
      '<span id="' + NOTICE_ID + '-count"></span>. ' +
      '작성 중인 내용이 있으면 <b>복사해 두신 뒤</b> 홈으로 이동해 주세요.' +
    '</div>' +
    '<button id="' + NOTICE_ID + '-reload" style="background:#fff;color:#b91c1c;border:0;border-radius:6px;' +
    'padding:8px 16px;font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap;">홈으로 이동</button>';

  document.body.appendChild(bar);
  // 같은 URL 로 새로고침하면 안 된다: 현재 주소는 이전 채널의 리소스(예: /project/detail?id=271)라
  // 새 채널 권한으로는 조회에 실패해 다시 오류가 난다. 채널 무관한 홈(/)으로 보낸다.
  document.getElementById(NOTICE_ID + "-reload")
    ?.addEventListener("click", () => { window.location.href = "/"; });
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "channelState" && e.oldValue !== e.newValue) {
      externalChannelSwitch = { from: e.oldValue, to: e.newValue };

      if (isChannelAgnosticPage()) {
        // sysadmin 등 채널 무관 화면은 그대로 사용 가능 — 끊지 않는다.
        console.warn(`[CHANNEL_SWITCHED_IN_ANOTHER_TAB] ${e.oldValue} → ${e.newValue} : 채널 무관 화면이라 계속 사용합니다.`);
        return;
      }

      console.warn(`[CHANNEL_SWITCHED_IN_ANOTHER_TAB] ${e.oldValue} → ${e.newValue} : 이 탭의 API 요청을 중단합니다.`);
      // 요청을 기다리지 않고 즉시 안내한다. 그래야 화면 이동으로 뜨는 GET 들이 나가기 전에 막힌다.
      showChannelSwitchNotice();
    }
  });
}

const removeChannelSwitchNotice = () => {
  if (typeof document === "undefined") return;
  document.getElementById(NOTICE_ID)?.remove();
};

/* ------------------------------------------------------------------------- *
 * 서버에 닿지 못한 경우(502 / 504)
 * ------------------------------------------------------------------------- */

const UNREACHABLE_ID = "omoplay-server-unreachable-notice";

/**
 * 502/504 안내를 **React 밖 DOM 으로 직접** 넣는다.
 *
 * 🔴 컴포넌트로 만들면 안 되는 이유가 이 안내의 존재 이유다 (2026-09-14 신고).
 *
 * `GET /account` 가 실패하면 `getAccount` 가 `{}` 를 돌려주고 `accountSelector` 도 `{}` 가 된다.
 * 그 값으로 `Layout` 이 렌더되다가 예외가 나면 — **이 앱에는 ErrorBoundary 가 한 곳도 없다** —
 * React 가 트리 전체를 언마운트해 `#root` 가 비어버린다. 즉 **문구 없는 완전한 흰 화면**이다.
 * (관리자가 고객사 세션을 오가며 작업할 때 백엔드 파드 재시작 구간에 걸리면 그 모습이 된다.
 *  백엔드는 운영에서 파드 1개라 재시작·배포 중 GCLB 가 502 를 낸다.)
 *
 * `document.body` 에 직접 붙이면 `#root` 가 비어도 안내가 남는다. 그래서 컴포넌트가 아니다.
 *
 * ⚠️ '다시 시도' 는 **현재 URL 새로고침**이다. 채널이 바뀐 상황이 아니라 서버가 잠깐 없었던 것이므로
 *    홈으로 보낼 이유가 없다 — 보고 있던 화면으로 돌아가는 것이 맞다.
 */
const showServerUnreachableNotice = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(UNREACHABLE_ID)) return;

  const bar = document.createElement("div");
  bar.id = UNREACHABLE_ID;
  bar.setAttribute("style",
    "position:fixed;top:0;left:0;right:0;z-index:2147483647;background:#b45309;color:#fff;" +
    "padding:10px 16px;display:flex;align-items:center;gap:12px;flex-wrap:wrap;" +
    "font-size:13px;line-height:1.6;box-shadow:0 2px 12px rgba(0,0,0,.3);");
  bar.innerHTML =
    '<div style="flex:1;min-width:280px;">' +
      '<b>서버에 일시적으로 연결할 수 없습니다.</b> ' +
      '잠시 후 다시 시도해 주세요. 이 화면의 요청은 모두 중단했습니다.' +
    '</div>' +
    '<button id="' + UNREACHABLE_ID + '-retry" style="background:#fff;color:#b45309;border:0;border-radius:6px;' +
    'padding:8px 16px;font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap;">다시 시도</button>';

  document.body.appendChild(bar);
  document.getElementById(UNREACHABLE_ID + "-retry")
    ?.addEventListener("click", () => { window.location.reload(); });
};

/* ------------------------------------------------------------------------- *
 * 다른 채널의 리소스를 조회한 경우(404 + ResultCode.NOT_FOUND)
 * ------------------------------------------------------------------------- */

/**
 * 안내와 이동을 **한 번만** 하기 위한 플래그.
 *
 * 상세 화면 하나가 조회를 병렬로 여럿 부른다 — 실제 사례: `/project/detail?id=816` 은
 * `detail`·`detail/events`·`detail/duplicates` 를 같은 밀리초에 보낸다.
 * 플래그가 없으면 `alert` 이 3번 뜬다.
 */
let resourceNotFoundHandled = false;

/**
 * 이 요청을 막아야 하는가.
 * 판정은 **요청 시점의 경로** 기준이라, SPA 로 채널 무관 화면(/sysadmin) ↔ 일반 화면을 오가도 그때그때 맞게 동작한다.
 * 채널 무관 화면으로 이동했다면 남아 있던 배너도 정리한다(오해 방지).
 */
const isChannelSwitched = () => {
  if (externalChannelSwitch === null) return false;
  if (isChannelAgnosticPage()) {
    removeChannelSwitchNotice();
    return false;
  }
  return true;
};

/**
 * 중단된 요청의 반환값.
 *
 * 예외를 던지거나 에러 객체를 반환하지 않는 이유:
 *  - throw → 호출부 대부분이 catch 하지 않아 처리되지 않은 rejection(CRA 오류 오버레이)이 된다.
 *  - 에러 객체 반환 → `return res.data` 패턴에서 undefined 가 흘러가 `.then(({data}) => ...)` 에서 TypeError.
 * 따라서 **영원히 대기하는 Promise** 로 호출부의 후속 로직을 조용히 멈춘다.
 * 화면은 이미 안내 오버레이로 덮여 있고, 사용자는 새로고침으로만 빠져나간다.
 */
const blockSwitchedChannelRequest = () => {
  blockedRequestCount += 1;
  showChannelSwitchNotice();
  return new Promise(() => {});
};

/**
 * 새로고침·페이지 이탈 중 표시.
 *
 * 채널 전환을 감지하기 **전에 이미 나가 있던 요청**은 새로고침 시점에 브라우저가 취소하고,
 * 그 catch 가 `checkAuthorization` → axios 에러 객체 → 호출부 `return res.data`(undefined)
 * → `.then(({data}) => ...)` 순으로 흘러 "Cannot destructure property 'data' of 'undefined'" 를 만든다.
 * 이미 버려질 응답이므로 호출부 로직을 아예 실행시키지 않는다.
 */
let unloading = false;

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => { unloading = true; });
}

export const fetchApi = {
  get: async (url, skipLoading = false) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();  // 읽기도 차단(타 고객사 데이터 조회·오류 연쇄 방지)
    try {
      return await instance.get(url, {
        headers: getTokenHeader(),
        skipLoading: skipLoading
      });
    } catch (res) {
      console.log("err", res)
      return checkAuthorization(res);
    }
  },
  getBlob: async (url) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();
    try {
      return await instance.get(url, {
        headers: getTokenHeader(),
        responseType: "blob"
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  },
  post: async (url, data, skipLoading = false) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();  // 타 탭 채널 전환 시 쓰기 차단(다른 고객사 저장 방지)
    try {
      return await instance.post(url, data, {
        headers: getTokenHeader(),
        skipLoading: skipLoading
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  },
  multipart_post: async (url, data, skipLoading = false) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();
    try {
      return await instance.post(url, data, {
        headers: {
          ...(getTokenHeader()),
          "Content-Type": "multipart/form-data"
        },
        skipLoading: skipLoading
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  },

  put: async (url, data) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();
    try {
      return await instance.put(url, data, {
        headers: getTokenHeader(),
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  },

  delete: async (url) => {
    if (isChannelSwitched()) return blockSwitchedChannelRequest();
    try {
      return await instance.delete(url, {
        headers: getTokenHeader(),
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  },

  source: (url) => {
    try {
      return new EventSourcePolyfill(url, {
        headers: {
          ...(getTokenHeader()),
          "Accept": "text/event-stream",
          "Content-Type": "text/event-stream",
        },
        heartbeatTimeout: 300000,
      });
    } catch (res) {
      return checkAuthorization(res);
    }
  }
};

/**
 * **로그인 없이 열려야 하는 화면들.** 401 이 나도 `/login` 으로 보내지 않는다.
 *
 * ## 🔴 새 공개 화면을 만들면 반드시 여기에 추가할 것
 *
 * 이 목록에 없으면 화면 자체는 잘 만들어도 **열리지 않는다.** `App` 이 모든 경로에서
 * `accountSelector`(→ `GET /account`)를 부르고, 비로그인 상태에서 그것이 401 이라
 * 이 함수가 로그인 화면으로 돌려보낸다.
 *
 * 2026-09-08 에 정확히 그 실패가 났다 — 사업자번호 확인 카드등록 링크(`/card/register`)를
 * 시크릿 창에서 열었더니 **로그인을 요구했다.** 로그인이 필요하면 그 링크의 존재 이유가 사라진다.
 *
 * ⚠️ **비교는 정확히 일치(===)다.** 하위 경로는 걸리지 않는다 —
 * 예: `/card/register` 는 되지만 `/card/register/step2` 는 안 된다. 그런 화면을 만들면 여기 추가할 것.
 *
 * 종전에는 `&&` 로 이어진 `!==` 열 줄이었다. 항목을 추가할 때 한 줄을 빼먹기 쉽고,
 * 빠뜨려도 **그 화면을 비로그인으로 열어보지 않으면 드러나지 않는다**(위 실패가 그랬다).
 */
export const PUBLIC_PATHS = [
  "/login",
  "/account/join",
  "/account/find/id",
  "/account/find/pwd",
  "/agent/tutorial",
  "/verficiation/test",     // ⚠️ 오타가 아니라 실제 라우트 이름이다(App.js 와 같아야 한다)
  // 사업자번호 확인 카드등록 링크(2026-09-08). 고객이 **로그인 없이** 여는 것이 목적이다.
  // ⛔ 구 `/direct/payments`·`/payments/toss/billing/direct` 는 같은 날 제거했다 —
  //    URL 의 accountId 를 그대로 신뢰해 값만 바꾸면 남의 계정에 카드를 붙일 수 있었다.
  "/card/register",
  "/payments/toss/billing/link",
  // 고지서 열람 링크(2026-10-02). 결제 예정 안내 문자를 받은 사장님이 **로그인 없이** 연다.
  "/invoice/view",
  "/error",
  "/none",
  "/advanced/coupon",
  "/advanced/coupon/usage",
  "/payment/success",
];

/** 지금 보고 있는 화면이 비로그인 공개 화면인가. */
const isPublicPath = () =>
  typeof window !== "undefined" && PUBLIC_PATHS.includes(window.location.pathname);

const checkAuthorization = (data) => {
  // 채널 전환 감지 후 / 페이지 이탈 중의 실패는 이미 무의미한 응답이다.
  // 에러 객체를 그대로 돌려주면 호출부의 `return res.data`(undefined) → `.then(({data}) => ...)` 에서
  // TypeError 가 연쇄되므로, 미해결 Promise 로 후속 로직 자체를 멈춘다.
  if (isChannelSwitched() || unloading) return new Promise(() => {});

  // 백엔드 검증 실패(400 / ResultCode.REQUEST_DATA=4000)는 사유를 사용자에게 보여주고 후속 로직을 멈춘다.
  // 그렇지 않으면 호출부가 `await api(...); alert("수정 되었습니다")` 를 그대로 실행해
  // **저장되지 않았는데 저장된 것으로 오인**한다.
  // 해당 경로: 랜딩 URL 스킴 검증(MessageUrlValidationException), 채널 소유권 검증(ChannelOwnershipException).
  if (data?.response?.status === 400 && data?.response?.data?.code === 4000) {
    alert(data.response.data.data || "요청 값이 올바르지 않습니다.");
    return new Promise(() => {});
  }

  /*
    권한 없음(403) — 사유를 알리고 **후속 로직을 멈춘다**. (2026-09-09 추가)

    왜 필요한가: 종전에는 403 이 아래로 그대로 흘러 호출부의 `res.status === 200` 검사에서
    `undefined !== 200` 이 되고, 많은 api 래퍼가 그 자리에서 `[]` 나 `undefined` 를
    **정상 반환값처럼** 돌려준다. 그러면 호출부가 `alert("완료되었습니다")` 를 그대로 실행한다 —
    실제 예: 'AI 빠른 자동화 일괄 세팅' 은 403 을 받고도 "자동화 세팅이 완료되었습니다" 를
    띄우고 새로고침했다. **아무것도 만들어지지 않았는데 성공으로 보인다.**
    (다른 래퍼는 `return res.data` 라 `undefined` 구조분해에서 TypeError 로 죽는다.)

    지금 403 이 나올 수 있는 경로는 전부 **내부 관리자 전용**이다 —
    `/sysadmin`·`/system`·`/wizard` 하위, 그리고 AI 생성 2기능
    (`/ai/generate/project`·`/ai/generate/event`·`/automate-message/ai/generate`).
    비관리자가 이들을 부르는 상황은 (a) 화면이 캐시된 옛 JS 인 탭 (b) 직접 호출 뿐이므로,
    조용히 실패하는 것보다 사유를 보여주는 쪽이 맞다.

    401 과 달리 로그인으로 보내지 않는다 — 토큰은 유효하고 권한만 없다.
    보내면 로그인 → 원래 화면 → 다시 403 으로 도는 것처럼 보인다.

    ⚠️ 이 처리는 `_ai-runs/2026-08-27_002_point_only_account_permission/001_current_permission_model.md`
       §2.3 의 "새 권한 계층은 403 을 쓰고 프론트 처리를 함께 넣어야 한다" 를 이행한 것이다.
  */
  if (data?.response?.status === 403) {
    alert("이 기능에 대한 권한이 없습니다.\n시스템 관리자에게 문의해 주세요.");
    return new Promise(() => {});
  }

  /*
    의존 서비스 일시 장애(503 / ResultCode.EXTERNAL_SERVICE_UNAVAILABLE=5040) — **리다이렉트하지 않는다.**
    (2026-09-11, 결정항목 D-8)

    왜 필요한가: `statistics-parsing-service` 가 죽으면 그 조회가 500 으로 나갔고, 아래 500 분기가
    `/notfound` 로 보내 **'성과 보드 > 온라인 매출' 화면이 열자마자 튕겼다.** 서버가 503 으로
    구분해 주기 시작했으니(`ExceptionAdvice.handleExternalStatisticsUnavailable`) 화면을 유지한다.

    🔴 **reject 로 돌려주는 것이 요점이다.** 그냥 에러 객체를 반환하면 api 래퍼가
    `res.status === 200` 이 아니라는 이유로 `[]`·`undefined` 를 **정상 반환값처럼** 돌려주고,
    화면은 '매출 0원 / 주문 없음' 을 사실처럼 표시한다(장애가 '데이터 없음' 으로 보인다).
    reject 면 호출부의 `.catch` 가 사유를 받아 안내 문구를 띄운다.

    🔴 이 분기를 추가하면 **503 을 받을 수 있는 모든 호출부에 `.catch` 가 있어야 한다.**
    현재 503 을 내는 경로는 `/statistics/orders`·`/statistics/orders/message` 뿐이고
    호출부는 `OnlineOrderUsers`·`UserDetail`·`VolatilityMessageDetail` 세 곳이다(모두 처리함).
    새로 503 을 내는 서버 경로를 만들 때는 그 호출부의 catch 를 같은 변경에 넣을 것.
  */
  if (data?.response?.status === 503) {
    const reason = data.response.data?.data;
    return Promise.reject(
      new Error(
        typeof reason === "string" && reason
          ? reason
          : "일시적으로 데이터를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요."
      )
    );
  }

  /*
    같은 채널에서 AI 생성이 이미 진행 중(409 / ResultCode.GENERATION_IN_PROGRESS=4092) — **503 과 같은 모양으로 reject.**
    (2026-10-29) 배경: 2분 넘는 AI 생성 POST 를 Chrome 이 HTTP/3 유휴 타임아웃(≈30초) 뒤 HTTP/2 로 자동 재전송해
    프로젝트·이벤트·혜택이 2세트 생겼다. 서버가 두 번째 요청을 409 로 거부하므로, 화면(AIAutoSetupPage·EventScreen 등
    AI 생성 호출부의 `.catch`)은 사유("이미 진행 중… 목록에서 확인")를 alert 하고 입력 상태로 돌아간다.
    🔴 reject 가 요점이다 — 그냥 반환하면 호출부 `.then` 이 '완료' 화면을 띄운다(실제로는 첫 요청이 아직 도는 중).
  */
  if (data?.response?.status === 409) {
    const reason = data.response.data?.data;
    return Promise.reject(
      new Error(
        typeof reason === "string" && reason
          ? reason
          : "같은 작업이 이미 진행 중입니다. 잠시 후 목록에서 결과를 확인해 주세요."
      )
    );
  }

  /*
    현재 고객사(채널)에 그 리소스가 없다(404 / ResultCode.NOT_FOUND=4040) — **사유를 알리고 홈으로 보낸다.**
    (2026-09-14, 관리자 세션 전환 시 500·흰화면 신고)

    언제 오는가: 관리자가 사이드바에서 고객사를 바꾸면 `Layout.js` 가 **같은 URL 을 다시 연다**.
    주소는 `/project/detail?id=816` 그대로인데 요청 헤더 `Channel-Id` 만 새 채널이 되므로,
    그 프로젝트가 새 채널 소유가 아니면 조회가 실패한다. 뒤로가기·오래된 북마크도 같은 경로다.

    🔑 **새로고침이 아니라 홈이다.** 현재 주소 자체가 이전 채널의 리소스라 새로고침하면 같은 실패가 반복된다
    (`showChannelSwitchNotice` 가 같은 이유로 홈으로 보낸다).

    🔑 **서버가 500 이 아니라 404 를 주기 시작한 것이 짝이다**(`ExceptionAdvice.handleProjectNotFound`).
    종전에는 500 → 아래 분기가 `/notfound` 로 보냈고, 그 화면은 빈 div 를 렌더한 뒤 홈으로 이동해서
    **이유 없이 흰 화면이 번쩍하고 튕기는 것**으로 보였다.

    🔴 400+4000 을 쓰지 않은 이유: 그쪽은 전역 alert 뒤 미해결 Promise 로 끝나 호출부 `finally` 가 돌지 않아
       스피너가 영구히 남는다. 여기서는 어차피 화면을 떠나므로 이동까지 함께 한다.
  */
  if (data?.response?.status === 404 && data?.response?.data?.code === 4040) {
    if (!resourceNotFoundHandled) {
      resourceNotFoundHandled = true;
      const reason = data.response.data?.data;
      alert(typeof reason === "string" && reason ? reason : "요청한 데이터를 찾을 수 없습니다.");
      window.location.href = "/";
    }
    return new Promise(() => {});
  }

  /*
    서버에 닿지 못했다(502 Bad Gateway / 504 Gateway Timeout) — **안내 배너 + 요청 중단.**
    (2026-09-14, 관리자 세션 전환 중 "502 + 흰화면" 신고)

    왜 필요한가: 종전에는 이 상태가 아래 어느 분기에도 걸리지 않아 맨 끝 `return data`(axios 에러 객체)로 빠졌다.
    그러면 `getAccount` 같은 래퍼가 `res.status === 200` 이 아니라는 이유로 **`{}` 를 정상 반환값처럼** 돌려주고,
    `Layout` 이 그 빈 객체로 렌더되다 터져 **문구 없는 흰 화면**이 됐다
    (memory `fetch-wrapper-turns-failure-into-success` 와 같은 뿌리).

    🔑 **502/504 는 우리 코드의 오류가 아니다.** 백엔드가 운영에서 파드 1개라(`hpa.yaml` maxReplicas: 1)
    재시작·배포 중 GCLB 에 healthy backend 가 0 이 되는 구간이 있고, 그때의 요청이 502 로 돌아온다.
    사용자가 할 수 있는 일은 잠시 후 재시도뿐이므로 문구도 그렇게 끝낸다.

    🔴 500 은 여기에 넣지 않는다 — 500 은 서버 안에서 처리하다 터진 것이고 `/notfound` 처리가 이미 있다.
       503 은 위에서 이미 `reject` 로 다룬다(의존 서비스 장애, 호출부 `.catch` 와 짝).
  */
  if (data?.response?.status === 502 || data?.response?.status === 504) {
    showServerUnreachableNotice();
    return new Promise(() => {});
  }

  if (data?.response?.status === 401 && !isPublicPath()) {
    window.location.href = "/login"
  } else if (data?.response?.status === 500) {
    window.location.href = "/notfound"
  }
  // 응답 없는 오류(타임아웃/네트워크 중단 등)는 data.response 가 undefined → 위 옵셔널 체이닝으로 크래시 방지.
  // 이 경우 아래에서 그대로 반환하여 호출부가 e.message 로 처리하게 한다(무한 스피너/크래시 방지).
  return data;
};

export const getTokenHeader = () => {
  const res = { ...DEFAULT_HEADER };
  const token = cookies.get("token");
  res.header["Authorization"] = `Bearer ${token}`;
  res.header["Channel-Id"] = localStorage.getItem("channelState")
  return res.header;
};
